import {BasicProps} from "../Main";
import React, {useEffect, useState} from "react";
import {
    Grid,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, MenuItem, useMediaQuery
} from "@mui/material";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {Colours, SamaggiTypography} from "../Components/SamaggiTypography";
import SamaggiPaper from "../Components/SamaggiPaper";
import {SamaggiDate, SamaggiField, SamaggiSelect} from "../Components/SamaggiFields";
import {SamaggiButton} from "../Components/SamaggiButton";
import axios from "axios";
import {handleError} from "../support";
import {useNavigate} from "react-router-dom";
import AWS from "aws-sdk";
import {v4 as UUIDv4} from "uuid";
import Typography from "@mui/material/Typography";
import theme from "../theme";

interface LooseObject {
    [key: string]: any
}

export type T = string | number | boolean | Array<T>;
export type DynamoDBData = Record<string, T | Array<DynamoDBData>>;

export enum BudgetRequestType {
    New,
    Extension
}

export class BudgetRequestData {
    readonly id: string;
    readonly type: BudgetRequestType;
    readonly budgetCode: string;
    readonly amount: number;
    readonly currency: string;
    readonly projection: string;
    readonly description: string;
    readonly name: string;
    readonly team: string;
    readonly role: string;
    readonly responded: boolean;
    readonly approved: boolean;
    readonly submittedTime: number;
    readonly approvedTime: number;
    readonly approvedBy: string;

    constructor(requestData: DynamoDBData) {
        this.id = requestData["id"] as string;
        this.type = requestData["type"] === "NEW" ? BudgetRequestType.New : BudgetRequestType.Extension;
        this.budgetCode = requestData["budget-code"] as string;
        this.amount = requestData["amount"] as number;
        this.currency = requestData["currency"] as string;
        this.projection = requestData["projection-url"] as string;
        this.description = requestData["description"] as string;
        this.name = requestData["name"] as string;
        this.team = requestData["team"] as string;
        this.role = requestData["role"] as string;
        this.responded = requestData["responded"] as boolean;
        this.approved = requestData["approved"] as boolean;
        this.approvedBy = requestData["approved-person"] as string;
        this.submittedTime = Date.parse(requestData["time-submitted"] as string);
        this.approvedTime = Date.parse(requestData["time-approved"] as string);
    }

    private update(token: string | null, approved: boolean, reason: string | null, onComplete: () => void) {
        reason = reason === null ? "" : reason;

        if (token === null) {
            alert("Invalid Session");
            return
        }

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/approve-budget", {"token": token, "requestID": this.id, "approve": approved, "reason": reason}).then((res) => {
            console.log(res, "APPROVE");
            onComplete();
            alert("Budget Status Updated");
        }).catch((error) => {
            console.log(error);
            onComplete();
            handleError(error, true);
        })
    }

    approve(token: string | null, onComplete: () => void) {
        this.update(token, true, null, onComplete);
    }

    reject(token: string | null, onComplete: () => void) {
        const reason = prompt("Please enter the reason for the rejection.", "Rejected due to missing information.");

        this.update(token, false, reason, onComplete);
    }

    statusDisplay(): string {
        if (!this.responded) {
            return "Pending"
        }
        if (this.approved) {
            return "Approved"
        } else {
            return "Rejected"
        }
    }
}

export class BudgetData {
    readonly id: string;
    readonly totalAmount: number;
    readonly remainingAmount: number;
    readonly currency: string;
    readonly role: string;
    readonly team: string;
    readonly requests: Array<ReimbursementData> = [];
    readonly open: boolean;
    readonly description: string;
    readonly pendingAmount: number;
    readonly createdTime: number;
    readonly requiresAction: number;

    constructor(data: DynamoDBData) {
        this.id = data["id"] as string;
        this.totalAmount = data["budget-total"] as number;
        this.currency = data["currency"] as string;
        this.role = data["role"] as string;
        this.team = data["team"] as string;
        this.open = data["open"] as boolean;
        this.description = data["description"] as string;
        this.remainingAmount = data["balance"] as number;
        this.pendingAmount = data["pending"] as number;
        this.createdTime = Date.parse(data["time-created"] as string);
        this.requiresAction = data["requires-action"] as number;

        (data["requests"] as Array<DynamoDBData>).forEach((request: DynamoDBData) => {
            this.requests.push(new ReimbursementData(request));
        })
    }

    close(token: string | null) {
        if (token === null) {
            alert("Invalid Session");
            return
        }

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/close-budget", {"token": token, "budgetCode": this.id}).then((res) => {
            console.log(res, "CLOSE BUDGET");

            alert("Budget Closed");
        }).catch((error) => {
            console.log(error);
            handleError(error, true);
        })
    }
}

export class ReimbursementData {
    readonly id: string;
    readonly name: string;
    readonly budgetCode: string;
    readonly receiptURL: string;
    readonly paymentDate: number;
    readonly accountNumber: string;
    readonly sortCode: string;
    readonly amount: number;
    readonly description: string;
    readonly currency: string;
    readonly responded: boolean;
    readonly approved: boolean;
    readonly approvedTime: number;
    readonly paid: boolean;
    readonly owner: string;
    readonly paidBy: string;
    readonly paidTime: number;
    readonly submittedTime: number;

    constructor(data: DynamoDBData) {
        this.id = data["id"] as string;
        this.budgetCode = data["budget-code"] as string;
        this.receiptURL = data["receipt"] as string;
        this.paymentDate = Date.parse(data["payment-date"] as string);
        this.accountNumber = (data["bank-account"] as string).split("//").at(-3) as string;
        this.sortCode = (data["bank-account"] as string).split("//").at(-2) as string;
        this.amount = data["amount"] as number;
        this.description = data["description"] as string;
        this.currency = data["currency"] as string;
        this.responded = data["responded"] as boolean;
        this.approved = data["approved"] as boolean;
        this.paid = data["paid"] as boolean;
        this.owner = data["owner"] as string;
        this.paidBy = data["paid-by"] as string;
        this.paidTime = Date.parse(data["paid-time"] as string);
        this.approvedTime = Date.parse(data["time-approved"] as string);
        this.submittedTime = Date.parse(data["time-submitted"] as string);
        this.name = data["name"] as string;
    }

    markPaid(token: string | null, onComplete: () => void) {
        if (token === null) {
            alert("Invalid Session");
            return
        }

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/set-paid", {"token": token, "reimbursementID": this.id}).then((res) => {
            console.log(res, "MARK PAID");
            onComplete();
            alert("Reimbursement Marked as Paid");
        }).catch((error) => {
            console.log(error);
            onComplete();
            handleError(error, true);
        })
    }

    private update(token: string | null, approved: boolean, reason: string | null, onComplete: () => void) {
        reason = reason === null ? "" : reason;

        if (token === null) {
            alert("Invalid Session");
            return
        }

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/approve-reimbursement", {"token": token, "reimbursementID": this.id, "approve": approved, "reason": reason}).then((res) => {
            console.log(res, "APPROVE");
            onComplete();
            alert("Reimbursement Status Updated");
        }).catch((error) => {
            console.log(error);
            onComplete()
            handleError(error, true);
        })
    }

    approve(token: string | null, onComplete: () => void) {
        this.update(token, true, null, onComplete);
    }

    reject(token: string | null, onComplete: () => void) {
        const reason = prompt("Please enter the reason for the rejection.", "Rejected due to missing information.");

        this.update(token, false, reason, onComplete);
    }

    statusDisplay(): string {
        if (!this.responded) {
            return "Pending"
        }
        if (this.paid) {
            return "Paid"
        }
        if (this.approved) {
            return "Approved"
        } else {
            return "Rejected"
        }
    }
}


export function truncate(input: string, n: number = 12) {
    if (input.length > n) {
        return input.substring(0, n) + '...';
    }
    return input;
}

export const NewReimbursements: React.FC<BasicProps> = (props) => {

    const [userID, setUserID] = useState("");

    const [reimbursementData, setReimbursementData] = useState<Array<ReimbursementData>>([]);
    const [budgetRequestData, setBudgetRequestData] = useState<Array<BudgetRequestData>>([]);
    const [budgetData, setBudgetData] = useState<Array<BudgetData>>([]);
    const [existingBudgetData, setExistingBudgetData] = useState<BudgetData>();
    const [budgetLoaded, setBudgetLoaded] = useState(false);
    const [budgetLoading, setBudgetLoading] = useState(false);

    const [file, setFile] = useState();
    const [fileName, setFileName] = useState("");

    const [action, setAction] = useState("NEW");
    const [amount, setAmount] = useState("0");
    const [currency, setCurrency] = useState("GBP");
    const [description, setDescription] = useState("");
    const [date, setDate] = useState(new Date());
    const [extensionID, setExtensionID] = useState("");

    const [request, setRequest] = useState("");
    const [finalAmount, setFinalAmount] = useState("0");

    const [bankData, setBankData] = useState([]);
    const [selectedBank, setSelectedBank] = useState("");

    const navigate = useNavigate();
    const vertical = useMediaQuery(theme.breakpoints.down("sm"));



    function loadBudget() {
        const token = localStorage.getItem("token");

        setBudgetLoading(true);

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/load-budget", {"token": token, "budgetID": request}).then((res) => {
            console.log(res, "LOAD BUDGET")
            setBudgetLoaded(true);
            setExistingBudgetData(res.data.data.data);
            setBudgetLoading(false);
        }).catch((error) => {
            console.log(error);
            handleError(error, false);
            setBudgetLoading(false);
        })
    }


    function load() {
        const token = localStorage.getItem("token");

        props.setShowCover(true);

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/list-budget-request", {"token": token}).then((res) => {
            console.log(res, "LIST BUDGET REQUEST")

            const data: Array<BudgetRequestData> = [];
            res.data.data.data.forEach((value: DynamoDBData)=> { data.push(new BudgetRequestData(value)); });
            setBudgetRequestData(data);
        }).catch((error) => {
            console.log(error);
            handleError(error, false);
        })

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/list-reimbursements", {"token": token}).then((res) => {
            console.log(res, "LIST REIMBURSEMENT");
            props.setShowCover(false);

            const data: Array<ReimbursementData> = [];
            res.data.data.data.forEach((value: DynamoDBData) => { data.push(new ReimbursementData(value)); })
            setReimbursementData(data);
        }).catch((error) => {
            props.setShowCover(false);
            handleError(error, false);
        })

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/list-budget", {"token": token}).then((res) => {
            console.log(res, "LIST BUDGET");
            props.setShowCover(false);

            const data: Array<BudgetData> = [];
            res.data.data.data.forEach((value: DynamoDBData) => { data.push(new BudgetData(value)); })
            setBudgetData(data);
        }).catch((error) => {
            props.setShowCover(false);
            handleError(error, false);
        })

        axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/list-bank-account", {"token": token}).then((res) => {
            console.log(res, "LIST BANK");
            props.setShowCover(false);

            setUserID(res.data.data.userID)
            setBankData(res.data.data.data);
        }).catch((error) => {
            props.setShowCover(false);
            handleError(error, false);
        })
    }

    async function uploadFile() {
        const bucketName = "samaggi-reimbursements";
        const bucketRegion = "eu-west-2";
        const IdentityPoolId = "eu-west-2:61ca4228-aff3-40ef-82b7-5b3c2e97af59";

        AWS.config.update({
            region: bucketRegion,
            credentials: new AWS.CognitoIdentityCredentials({
                IdentityPoolId: IdentityPoolId
            })
        });

        if (file === undefined) {
            alert("Please choose a file.");
            return
        }

        const re = /(?:\.([^.]+))?$/;
        const regex = re.exec(fileName)

        if (regex === null) {
            alert("Failed to get file name.")
            return
        }

        let fileExt = regex[1];

        if (fileExt === undefined) {
            fileExt = ".jpg";
        }

        const key = encodeURI(`${userID}/${UUIDv4().toString().replaceAll("-", "").slice(0, 14)}.${fileExt}`);

        const upload = new AWS.S3.ManagedUpload({
            params: {
                Bucket: bucketName,
                Key: key,
                Body: file
            }
        });

        const uploadPromise = upload.promise();

        props.setShowCover(true);

        uploadPromise.then(
            () => submit(key),
            function(err) {
                alert(err);
                props.setShowCover(false);
                return alert("There was an error uploading your file: " + err.message);
            }
        );
    }

    function submit(path: string) {
        const token = localStorage.getItem("token");

        if (action === "NEW") {
            let data: LooseObject;

            data = {
                "token": token,
                "amount": parseFloat(amount),
                "currency": currency,
                "description": description,
                "projectionURL": path
            }

            if ([parseFloat(amount), currency, description, path].some((x) => x === "" || x === 0)) {
                alert("You may not leave any field blank.");
                return
            }

            axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/request-budget", data).then((res) => {
                console.log(res);
                load()

                setAmount("0");
                setCurrency("GBP");
                setDescription("");
                setDate(new Date());
                setSelectedBank("");
                setFile(undefined);
                setFileName("");

            }).catch((error) => {
                props.setShowCover(false);
                alert("Submission Failed.");

                handleError(error, true);
            })
        } else if (action === "EXTEND") {
            let data: LooseObject;

            data = {
                "token": token,
                "amount": parseFloat(amount),
                "description": description,
                "projectionURL": path,
                "budgetID": extensionID
            }

            if ([parseFloat(amount), description, path, extensionID].some((x) => x === "" || x === 0)) {
                alert("You may not leave any field blank.");
                return
            }

            axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/extend-budget", data).then((res) => {
                console.log(res);
                load()

                setAmount("0");
                setCurrency("GBP");
                setDescription("");
                setDate(new Date());
                setSelectedBank("");
                setFile(undefined);
                setFileName("");

            }).catch((error) => {
                props.setShowCover(false);
                alert("Submission Failed.");

                handleError(error, true);
            })
        } else {
            const data = {
                "token": token,
                "budgetCode": request,
                "amount": parseFloat(finalAmount),
                "bankAccount": selectedBank,
                "description": description,
                "paymentDate": date.toString(),
                "receipt": path
            }

            if ([request, parseFloat(finalAmount), description, selectedBank, date.toString(), path].some((x) => x === "" || x === 0)) {
                alert("You may not leave any field blank.");
                return
            }

            axios.post("https://usvj99ipg7.execute-api.eu-west-2.amazonaws.com/Prod/finance/request-reimbursements", data).then((res) => {
                console.log(res);
                load()

                setFinalAmount("0");
                setRequest("");
                setCurrency("GBP");
                setDescription("");
                setDate(new Date());
                setSelectedBank("");
                setFile(undefined);
                setFileName("");

            }).catch((error) => {
                props.setShowCover(false);
                alert("Submission Failed.");

                handleError(error, true);
            })
        }

    }

    useEffect(() => {
        load() // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function showCover() {
        props.setShowCover(true);
    }

    function ReimbursementRow(props: { row: ReimbursementData, owner: boolean }) {
        const { row } = props;
        const [open, setOpen] = useState(false);

        return (
            <React.Fragment>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell align={"center"} sx={{padding: 0}}>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}
                            sx={{padding: "2px"}}
                        >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{truncate(row.description)}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{row.currency} {row.amount}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px", fontWeight: 600, color: (row.approved ? "#008600" : row.responded ? "#a80000" : "#000000")}}>{row.statusDisplay()}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={open} timeout="auto" unmountOnExit sx={{paddingLeft: 0, paddingRight: 0, paddingTop: 5, paddingBottom: 5}}>
                            <Grid container direction={"column"} spacing={4}>
                                <Grid item container direction={"column"} spacing={1.2}>
                                    <Grid item>
                                        <SamaggiTypography large bold colour={Colours.samaggiOrange}>
                                            General
                                        </SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>ID: </SamaggiTypography>{row.id}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Name: </SamaggiTypography>{row.name}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Description: </SamaggiTypography>{row.description}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
										<SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Bank Account: </SamaggiTypography>{row.accountNumber} {row.sortCode}</SamaggiTypography>
									</Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Submitted: </SamaggiTypography>{(new Date(row.submittedTime)).toString()}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small>
                                            <SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Status: </SamaggiTypography>
                                            {row.responded ? (row.approved ? "Approved": "Rejected") : "Pending"} ({row.responded ? (new Date(row.approvedTime).toString()) : (new Date(row.submittedTime).toString())})</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Time Paid: </SamaggiTypography>{row.paid ? (new Date(row.paidTime)).toString() : "Not Paid"}</SamaggiTypography>
                                    </Grid>
                                </Grid>
                                <Grid item container direction={"column"} spacing={1.2}>
                                    <Grid item>
                                        <SamaggiTypography large bold colour={Colours.samaggiOrange}>
                                            Files
                                        </SamaggiTypography>
                                    </Grid>
                                    <Grid item>
										<SamaggiButton fullWidth onClick={() => {window.open(row.receiptURL, "_blank")}}>View Receipt</SamaggiButton>
									</Grid>
                                </Grid>
                                {props.owner && !row.responded && <>
                                    <Grid item container direction={"column"} spacing={1.2}>
                                        <Grid item>
                                            <SamaggiTypography large bold colour={Colours.samaggiOrange}>
                                                Actions
                                            </SamaggiTypography>
                                        </Grid>
                                        <Grid item>
                                            <SamaggiButton fullWidth colour={"#008600"} hoverColour={"#005c00"} onClick={() => {row.approve(localStorage.getItem("token"), load); showCover()}}>Approve</SamaggiButton>
                                        </Grid>
										<Grid item>
											<SamaggiButton fullWidth colour={"#a80000"} hoverColour={"#780000"} onClick={() => {row.reject(localStorage.getItem("token"), load); showCover()}}>Reject</SamaggiButton>
										</Grid>
                                    </Grid>
                                </>}
                            </Grid>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    }

    function BudgetRow(props: { row: BudgetData }) {
        const { row } = props;
        const [open, setOpen] = useState(false);

        return (
            <React.Fragment>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell align={"center"} sx={{padding: 0}}>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}
                            sx={{padding: "2px"}}
                        >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{truncate(row.description)}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{row.currency} {row.totalAmount}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{row.open ? `${row.currency} ${row.remainingAmount}` : "Closed"}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px", color: (row.requiresAction === 0 ? "#008600" : "#a80000")}}>{row.requiresAction}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={open} timeout="auto" unmountOnExit sx={{paddingLeft: 0, paddingRight: 0, paddingTop: 5, paddingBottom: 5}}>
                            <Grid container direction={"column"} spacing={4}>
                                <Grid item container direction={"column"} spacing={1.2}>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Budget Code: </SamaggiTypography>{row.id}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography large bold colour={Colours.samaggiOrange}>
                                            Reimbursements
                                        </SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        {row.requests.length !== 0 && <>
                                        <TableContainer component={Paper}>
                                            <Table aria-label="collapsible table" size={"small"}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell/>
                                                        <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Desc." : "Description"}</TableCell>
                                                        <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Amt." : "Amount"}</TableCell>
                                                        <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Stat." : "Status"}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {row.requests.sort((a,b) => b.submittedTime - a.submittedTime).map((row) => (
                                                        <ReimbursementRow key={row.submittedTime} row={row} owner={true} />
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        </>}
                                        {row.requests.length === 0 && <>
                                            <SamaggiTypography center>Reimbursement Requests will Appear Here</SamaggiTypography>
                                        </>}
                                    </Grid>
                                    {row.open && <>
                                    <Grid item container direction={"column"} spacing={1.2}>
                                        <Grid item>
                                            <SamaggiTypography large bold colour={Colours.samaggiOrange}>
                                                Actions
                                            </SamaggiTypography>
                                        </Grid>
                                        <Grid item>
                                            <SamaggiButton fullWidth onClick={() => {row.close(localStorage.getItem("token"))}}>Close Budget</SamaggiButton>
                                        </Grid>
                                    </Grid>
                                    </>}
                                </Grid>
                            </Grid>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    }

    function BudgetRequestRow(props: { row: BudgetRequestData }) {
        const { row } = props;
        const [open, setOpen] = useState(false);

        return (
            <React.Fragment>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                    <TableCell align={"center"} sx={{padding: 0}}>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}
                            sx={{padding: "2px"}}
                        >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{row.type === BudgetRequestType.New ? "New" : "Extend"}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{truncate(row.description)}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px"}}>{row.currency} {row.amount}</TableCell>
                    <TableCell align={"center"} sx={{padding: "2px", fontWeight: 600, color: (row.approved ? "#008600" : row.responded ? "#a80000" : "#000000")}}>{row.statusDisplay()}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={open} timeout="auto" unmountOnExit sx={{paddingLeft: 0, paddingRight: 0, paddingTop: 5, paddingBottom: 5}}>
                            <Grid container direction={"column"} spacing={4}>
                                <Grid item container direction={"column"} spacing={1.2}>
                                    <Grid item>
                                        <SamaggiTypography large bold colour={Colours.samaggiOrange}>
                                            General
                                        </SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>ID: </SamaggiTypography>{row.id}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Description: </SamaggiTypography>{row.description}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small><SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Submitted: </SamaggiTypography>{(new Date(row.submittedTime)).toString()}</SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiTypography small>
                                            <SamaggiTypography bold small inline colour={Colours.samaggiOrange}>Status: </SamaggiTypography>
                                            {row.responded ? (row.approved ? "Approved": "Rejected") : "Pending"} ({row.responded ? (new Date(row.approvedTime).toString()) : (new Date(row.submittedTime).toString())})</SamaggiTypography>
                                    </Grid>
                                </Grid>
                                <Grid item container direction={"column"} spacing={1.2}>
                                    <Grid item>
                                        <SamaggiTypography large bold colour={Colours.samaggiOrange}>
                                            Files
                                        </SamaggiTypography>
                                    </Grid>
                                    <Grid item>
                                        <SamaggiButton fullWidth onClick={() => {window.open(row.projection, "_blank")}}>View Projection</SamaggiButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    }


    function handleFileChange(e: any) {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setFileName(e.target.files[0].name);
        }
    }

    function handleRequestChange(id: string) {
        setRequest(id);
    }

    return (
        <Grid container direction={"column"} spacing={2}>
            <Grid item>
                <SamaggiTypography large bold colour={Colours.black}>My Budgets</SamaggiTypography>
            </Grid>
            <Grid item>
                {budgetData.length > 0 && <TableContainer component={Paper}>
                    <Table aria-label="collapsible table" size={"small"}>
                        <TableHead>
                            <TableRow>
                                <TableCell/>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Desc." : "Description"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Total Amt." : "Total Amount"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Remaining" : "Remaining Amount"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Act." : "Actions Required"}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {budgetData.sort((a,b) => b.createdTime - a.createdTime).map((row) => (
                                <BudgetRow key={row.createdTime} row={row} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>}
                {budgetData.length === 0 && <SamaggiTypography center>
                    Your Budgets Will Appear Here
                </SamaggiTypography>}
            </Grid>
            <Grid item>
                <SamaggiTypography large bold colour={Colours.black}>My Reimbursements</SamaggiTypography>
            </Grid>
            <Grid item>
                {reimbursementData.length > 0 && <TableContainer component={Paper}>
                    <Table aria-label="collapsible table" size={"small"}>
                        <TableHead>
                            <TableRow>
                                <TableCell/>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Desc." : "Description"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Amt." : "Amount"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Stat." : "Status"}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reimbursementData.sort((a,b) => b.submittedTime - a.submittedTime).map((row) => (
                                <ReimbursementRow key={row.submittedTime} row={row} owner={false} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>}
                {reimbursementData.length === 0 && <SamaggiTypography center>
					Your Reimbursement Requests Will Appear Here
				</SamaggiTypography>}
            </Grid>
            <Grid item>
                <SamaggiTypography large bold colour={Colours.black}>My Budget Requests</SamaggiTypography>
            </Grid>
            <Grid item>
                {budgetRequestData.length > 0 && <TableContainer component={Paper}>
                    <Table aria-label="collapsible table" size={"small"}>
                        <TableHead>
                            <TableRow>
                                <TableCell/>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Typ." : "Type"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Desc." : "Description"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Amt." : "Amount"}</TableCell>
                                <TableCell align={"center"} sx={{padding: "1px"}}>{vertical ? "Stat." : "Status"}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {budgetRequestData.sort((a,b) => b.submittedTime - a.submittedTime).map((row) => (
                                <BudgetRequestRow key={row.submittedTime} row={row} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>}
                {budgetRequestData.length === 0 && <SamaggiTypography center>
					Your Budget Requests Will Appear Here
				</SamaggiTypography>}
            </Grid>
            <Grid item>
                <SamaggiButton fullWidth colour={Colours.samaggiOrange} hoverColour={Colours.darkGray} onClick={() => {props.setShowCover(true); load()}}>Reload</SamaggiButton>
            </Grid>
            <Grid item>
                <SamaggiPaper style={{width: "97%"}}>
                    <Grid container direction={"column"} spacing={2}>
                        <Grid item>
                            <SamaggiSelect value={action} onChange={(e) => {setAction(e.target.value as string); setBudgetLoaded(false)}} label={"Action"}>
                                <MenuItem value={"NEW"}>Create Budget Code</MenuItem>
                                <MenuItem value={"EXTEND"}>Extend Existing Budget Code</MenuItem>
                                <MenuItem value={"EXISTING"}>Submit Reimbursement Request</MenuItem>
                            </SamaggiSelect>
                        </Grid>
                        {(action === "NEW" || action === "EXTEND") && <>
                            {action === "EXTEND" && <>
								<Grid item>
									<SamaggiSelect label={"Budget to Extend"} value={extensionID} onChange={(e) => {setExtensionID(e.target.value as string)}}>
                                        {budgetData.filter((item) => item.open).sort((a,b) => (b.id > a.id ? 1 : 0)).map((item) =>
                                            <MenuItem value={item.id}>{truncate(item.id)} — {truncate(item.description, 15)}</MenuItem>
                                        )}
									</SamaggiSelect>
								</Grid>
                            </>}
							<Grid item container direction={"row"} spacing={2}>
								<Grid item xs={6}>
									<SamaggiField label={"Amount"} value={amount} inputProps={{maxLength: 13, step: ".01"}} onChange={(e) => {setAmount(e.target.value)}} type={"number"}/>
								</Grid>
								<Grid item xs={6}>
									<SamaggiSelect label={"Currency"} value={currency} onChange={(e) => {setCurrency(e.target.value as string)}}>
										<MenuItem value={"GBP"}>GBP</MenuItem>
										<MenuItem value={"THB"}>THB</MenuItem>
										<MenuItem value={"USD"}>USD</MenuItem>
									</SamaggiSelect>
								</Grid>
							</Grid>
							<Grid item>
								<SamaggiField label={"Description of Payment"} value={description} onChange={(e) => {setDescription(e.target.value)}} type={"text"}/>
							</Grid>
							<Grid item container direction={"column"} spacing={2}>
								<Grid item>
									<Typography sx={{fontWeight: "600"}}>Projection and/or Invoice</Typography>
								</Grid>
								<Grid item>
									<input type={"file"} accept="image/png, image/jpeg, application/pdf" id="file-upload" onChange={(e) => {handleFileChange(e)}} />
								</Grid>
							</Grid>
							<Grid item>
								<SamaggiButton fullWidth onClick={() => {uploadFile()}}>{"Request Budget Code"}</SamaggiButton>
							</Grid>
						</>}
                        {action === "EXISTING" && <>
							<Grid item>
								<SamaggiField label={"Budget Code"} value={request} onChange={(e) => {handleRequestChange(e.target.value as string); setBudgetLoaded(false)}} type={"text"}/>
							</Grid>
							<Grid item>
								<SamaggiButton fullWidth onClick={() => {loadBudget()}}>{budgetLoading ? "Checking..." : "Check"}</SamaggiButton>
							</Grid>
                            {budgetLoaded && <>
                                <Grid item container direction={"row"} spacing={2}>
                                    <Grid item xs={6}>
                                        <SamaggiField label={"Reimbursement Amount"} value={finalAmount} inputProps={{maxLength: 13, step: ".01"}} onChange={(e) => {setFinalAmount(e.target.value)}} type={"number"}/>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <SamaggiSelect disabled label={"Currency"} value={existingBudgetData?.currency}>
                                            <MenuItem value={"GBP"}>GBP</MenuItem>
                                            <MenuItem value={"THB"}>THB</MenuItem>
                                            <MenuItem value={"USD"}>USD</MenuItem>
                                        </SamaggiSelect>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <SamaggiField label={"Payment Description"} value={description} onChange={(e) => {setDescription(e.target.value)}} type={"text"}/>
                                </Grid>
                                <Grid item>
                                    <SamaggiDate label={"Payment Date"} value={date} onChange={(e) => setDate((e === null) ? new Date() : e)}/>
                                </Grid>
                                <Grid item container direction={"column"} spacing={2}>
                                    <Grid item>
                                        <Typography sx={{fontWeight: "600"}}>Receipt/Proof of Purchase</Typography>
                                    </Grid>
                                    <Grid item>
                                        <input type={"file"} accept="image/png, image/jpeg, application/pdf" id="file-upload" onChange={(e) => {handleFileChange(e)}} />
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <SamaggiSelect label={"Bank Account"} value={selectedBank} onChange={(e) => {setSelectedBank(e.target.value as string)}}>
                                        {bankData.map((item) => (
                                            <MenuItem value={`${item["id"]}//${item["account-number"]}//${item["country"] === "THA" ? item["bank-name"] : item["sort-code"]}//${item["country"]}`}>{item["account-number"]} — {item["country"] === "THA" ? item["bank-name"] : item["sort-code"]} — {item["country"] === "THA" ? "Thailand" : "United Kingdom"}</MenuItem>
                                        ))}
                                    </SamaggiSelect>
                                </Grid>
                                <Grid item>
                                    <SamaggiTypography xsmall italic center>Once you submit your reimbursement request, the budget owner will be requested to confirm the payment.</SamaggiTypography>
                                </Grid>
                                <Grid item>
                                    <SamaggiButton fullWidth onClick={() => {uploadFile().then(r => {console.log(`Uploaded ${r}`)})}}>Request Reimbursement</SamaggiButton>
                                </Grid>
							</>}
						</>}
                    </Grid>
                </SamaggiPaper>
            </Grid>
            <Grid item>
                <SamaggiButton fullWidth colour={Colours.gray} hoverColour={Colours.darkGray} onClick={() => {navigate("/")}}>Back to Staff Portal</SamaggiButton>
            </Grid>
        </Grid>
    );
}
